import { useMemo, useRef, useState } from "react";
import { Plus, X } from "lucide-react";
import type {
  DetailGenre,
  DetailSuggestedGenre,
  EntityDetail,
} from "#shared/schemas/entityDetail.ts";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@web/components/ui/context-menu.tsx";
import { useCreateGenre, useGenresFlat } from "@web/features/library/api.ts";
import { cn } from "@web/lib/utils.ts";

/** Hers use the global 5-chip rule; suggestions separately cap at 6. */
export const GENRE_CHIP_VISIBLE = 5;
export const SUGGESTION_CHIP_VISIBLE = 6;

export function GenreChipRow({
  detail,
  disabled,
  onAssign,
  onRemove,
  onAdopt,
  onDismiss,
}: {
  detail: EntityDetail;
  disabled: boolean;
  onAssign: (genre: DetailGenre) => void;
  onRemove: (genreId: string) => void;
  onAdopt: (suggestion: DetailSuggestedGenre) => void;
  onDismiss: (
    suggestion: DetailSuggestedGenre,
    scope: "entity" | "global",
  ) => void;
}) {
  const editable = detail.supports.genres && !detail.genres_computed;
  const genres = useGenresFlat();
  const createGenre = useCreateGenre();
  const [hersExpanded, setHersExpanded] = useState(false);
  const [suggestionsExpanded, setSuggestionsExpanded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const assignedIds = useMemo(
    () => new Set(detail.genres.map((g) => g.genre_id)),
    [detail.genres],
  );

  const hers = detail.genres;
  const suggestions = editable ? (detail.suggested_genres ?? []) : [];

  const visibleHers = hersExpanded ? hers : hers.slice(0, GENRE_CHIP_VISIBLE);
  const hiddenHers = Math.max(0, hers.length - GENRE_CHIP_VISIBLE);
  const visibleSuggestions = suggestionsExpanded
    ? suggestions
    : suggestions.slice(0, SUGGESTION_CHIP_VISIBLE);
  const hiddenSuggestions = Math.max(0, suggestions.length - SUGGESTION_CHIP_VISIBLE);

  const pickerMatches = useMemo(() => {
    const term = query.trim().toLowerCase();
    return (genres.data ?? [])
      .filter((g) => !assignedIds.has(g.id))
      .filter((g) => (term === "" ? true : g.name.toLowerCase().includes(term)))
      .slice(0, 8);
  }, [genres.data, assignedIds, query]);

  const canCreate =
    query.trim().length > 0 &&
    !(genres.data ?? []).some((g) => g.name.toLowerCase() === query.trim().toLowerCase());

  function assignFromPicker(id: string, name: string, slug: string, color: string | null) {
    onAssign({
      genre_id: id,
      name,
      slug,
      color,
      source: "user",
      confidence: null,
    });
    setQuery("");
    setPickerOpen(false);
  }

  async function createAndAssign() {
    const name = query.trim();
    if (!name) return;
    const created = await createGenre.mutateAsync(name);
    assignFromPicker(created.id, created.name, created.slug, created.color);
  }

  if (!editable && hers.length === 0) return null;

  return (
    <section className="space-y-1.5" aria-label="Genres">
      <div className="flex flex-wrap items-center gap-1.5">
        {visibleHers.map((genre) => (
          <UserGenreChip
            key={`u-${genre.genre_id}`}
            genre={genre}
            editable={editable}
            computed={detail.genres_computed}
            disabled={disabled}
            onRemove={() => onRemove(genre.genre_id)}
          />
        ))}

        {!hersExpanded && hiddenHers > 0 && (
          <button
            type="button"
            className="rounded-full bg-panel px-2 py-1 text-xs text-muted hover:text-text"
            onClick={() => setHersExpanded(true)}
          >
            +{hiddenHers}
          </button>
        )}
        {hersExpanded && hiddenHers > 0 && (
          <button
            type="button"
            className="rounded-full px-2 py-1 text-xs text-faint hover:text-text"
            onClick={() => setHersExpanded(false)}
          >
            Show less
          </button>
        )}

        {visibleSuggestions.map((suggestion) => (
          <SuggestedGenreChip
            key={`s-${suggestion.key}`}
            suggestion={suggestion}
            disabled={disabled}
            onAdopt={() => onAdopt(suggestion)}
            onDismissEntity={() => onDismiss(suggestion, "entity")}
            onBlockGlobal={() => onDismiss(suggestion, "global")}
          />
        ))}

        {!suggestionsExpanded && hiddenSuggestions > 0 && (
          <button
            type="button"
            className="rounded-full border border-dashed border-line px-2 py-1 text-xs text-faint hover:text-muted"
            onClick={() => setSuggestionsExpanded(true)}
          >
            +{hiddenSuggestions}
          </button>
        )}
        {suggestionsExpanded && hiddenSuggestions > 0 && (
          <button
            type="button"
            className="rounded-full px-2 py-1 text-xs text-faint hover:text-text"
            onClick={() => setSuggestionsExpanded(false)}
          >
            Show less
          </button>
        )}

        {editable && (
          <div className="relative">
            <button
              type="button"
              disabled={disabled}
              aria-label="Add genre"
              className="inline-flex size-7 items-center justify-center rounded-full border border-dashed border-line text-faint hover:border-muted hover:text-text disabled:opacity-50"
              onClick={() => {
                setPickerOpen((o) => !o);
                window.setTimeout(() => inputRef.current?.focus(), 0);
              }}
            >
              <Plus className="size-3.5" />
            </button>
            {pickerOpen && (
              <div className="absolute top-full left-0 z-20 mt-1 w-56 rounded-md border border-line bg-panel-elevated p-2 shadow-lg">
                <input
                  ref={inputRef}
                  type="search"
                  value={query}
                  disabled={disabled}
                  placeholder="Search or create…"
                  aria-label="Search genres"
                  className="h-8 w-full rounded-md border border-line bg-panel px-2 text-sm text-text placeholder:text-faint focus-visible:outline-none"
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      e.preventDefault();
                      setPickerOpen(false);
                    }
                    if (e.key === "Enter" && canCreate) {
                      e.preventDefault();
                      void createAndAssign();
                    }
                  }}
                />
                <div className="mt-1 max-h-40 overflow-y-auto">
                  {pickerMatches.map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      disabled={disabled}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-muted hover:bg-row-hover hover:text-text"
                      onClick={() => assignFromPicker(g.id, g.name, g.slug, g.color)}
                    >
                      <span className="size-1 shrink-0 rounded-full bg-muted" aria-hidden />
                      {g.name}
                    </button>
                  ))}
                  {canCreate && (
                    <button
                      type="button"
                      disabled={disabled || createGenre.isPending}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-accent hover:bg-row-hover"
                      onClick={() => void createAndAssign()}
                    >
                      Create “{query.trim()}”
                    </button>
                  )}
                  {pickerMatches.length === 0 && !canCreate && (
                    <p className="px-2 py-1.5 text-xs text-faint">No matches</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function UserGenreChip({
  genre,
  editable,
  computed,
  disabled,
  onRemove,
}: {
  genre: DetailGenre;
  editable: boolean;
  computed: boolean;
  disabled: boolean;
  onRemove: () => void;
}) {
  return (
    <span
      className={cn(
        "group inline-flex max-w-full items-center gap-1.5 rounded-full bg-panel px-2 py-1 text-xs text-text",
      )}
    >
      {!computed && (
        <span className="size-1 shrink-0 rounded-full bg-muted" aria-hidden />
      )}
      <span className="truncate">{genre.name}</span>
      {editable && (
        <button
          type="button"
          disabled={disabled}
          aria-label={`Remove ${genre.name}`}
          className="shrink-0 text-faint opacity-0 transition-opacity group-hover:opacity-100 hover:text-text disabled:opacity-50"
          onClick={onRemove}
        >
          <X className="size-3" />
        </button>
      )}
    </span>
  );
}

function SuggestedGenreChip({
  suggestion,
  disabled,
  onAdopt,
  onDismissEntity,
  onBlockGlobal,
}: {
  suggestion: DetailSuggestedGenre;
  disabled: boolean;
  onAdopt: () => void;
  onDismissEntity: () => void;
  onBlockGlobal: () => void;
}) {
  const inTaxonomy = suggestion.genre_id !== null;
  const tip = inTaxonomy
    ? `${suggestion.source_label} · already in your genres — click to assign`
    : `${suggestion.source_label} — click to adopt · right-click for more`;
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <span
          className={cn(
            "group inline-flex max-w-full items-center gap-1 rounded-full border border-dashed bg-transparent px-2 py-1 text-xs",
            inTaxonomy
              ? "border-muted/40 text-muted"
              : "border-line text-faint",
          )}
        >
          <button
            type="button"
            disabled={disabled}
            title={tip}
            className={cn(
              "truncate disabled:opacity-50",
              inTaxonomy ? "hover:text-text" : "hover:text-muted",
            )}
            onClick={onAdopt}
          >
            {suggestion.name}
          </button>
          <button
            type="button"
            disabled={disabled}
            aria-label={`Hide ${suggestion.name} on this item`}
            title={`Hide on this item · ${suggestion.source_label}`}
            className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100 hover:text-text disabled:opacity-50"
            onClick={onDismissEntity}
          >
            <X className="size-3" />
          </button>
        </span>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem disabled={disabled} onSelect={onAdopt}>
          {inTaxonomy ? "Assign" : "Adopt"}
        </ContextMenuItem>
        <ContextMenuItem disabled={disabled} onSelect={onDismissEntity}>
          Hide on this item
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem
          disabled={disabled}
          variant="destructive"
          onSelect={onBlockGlobal}
        >
          Never suggest again
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
